import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";

import { Session, SessionPaymentStatus, SessionProgress } from "../../domain/Session";

const PILL = "px-2 py-0.5 text-xs font-medium rounded-full border";
const NEUTRAL = "bg-gray-50 text-gray-500 border-gray-100 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700";
const POSITIVE = "bg-green-50 text-green-600 border-green-100 dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/20";
const WARNING = "bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20";
const DANGER = "bg-red-50 text-red-600 border-red-100 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20";
const ACCENT = "bg-accent-tint text-accent-text border-accent/20";

const ANALYSIS_STYLES: Record<string, string> = {
    pending: NEUTRAL,
    processing: ACCENT,
    review: WARNING,
    completed: POSITIVE,
    failed: DANGER,
};

const PROGRESS_STYLES: Record<SessionProgress, string> = {
    pending: NEUTRAL,
    finished: POSITIVE,
    cancelled: DANGER,
};

const PAYMENT_STYLES: Record<SessionPaymentStatus, string> = {
    pending: WARNING,
    paid: POSITIVE,
};

/**
 * A session can be over without having been analyzed: the therapist closed
 * the review, the notes were too short, or the tool is not adopted. That is
 * not "completed" as far as the analysis goes, and it is not said as such.
 */
function analysisLabel(status: NonNullable<Session["status"]>, skippedReason?: string | null) {
    return status === "completed" && skippedReason
        ? { key: "session.statusOptions.notAnalyzed", style: NEUTRAL }
        : { key: `session.statusOptions.${status}`, style: ANALYSIS_STYLES[status] ?? NEUTRAL };
}

/** Where the AI is with the session's recording and notes. */
export function AnalysisStatusBadge({
    status,
    skippedReason,
}: {
    status?: Session["status"];
    skippedReason?: Session["analysis_skipped_reason"];
}) {
    const { t } = useTranslation();
    if (!status) return <>—</>;
    const { key, style } = analysisLabel(status, skippedReason);
    return <span className={`${PILL} ${style}`}>{t(key)}</span>;
}

/** Whether the session took place. */
export function SessionProgressBadge({ status = "pending" }: { status?: SessionProgress }) {
    const { t } = useTranslation();
    return <span className={`${PILL} ${PROGRESS_STYLES[status]}`}>{t(`session.progressOptions.${status}`)}</span>;
}

/** Whether it was paid for. */
export function PaymentStatusBadge({ status = "pending" }: { status?: SessionPaymentStatus }) {
    const { t } = useTranslation();
    return <span className={`${PILL} ${PAYMENT_STYLES[status]}`}>{t(`session.paymentOptions.${status}`)}</span>;
}

interface SessionStatusBadgesProps {
    session: Pick<Session, "status" | "session_status" | "analysis_skipped_reason">;
    /** The analysis status is the AI tool's: shown only to those who use it. */
    showAnalysis: boolean;
}

/**
 * A session's statuses for the lists: whether it took place and, for those
 * who use AI, where its analysis is. Payment stays on the session page — the
 * list is for finding a session, not for reading all it holds.
 */
export function SessionStatusBadges({ session, showAnalysis }: SessionStatusBadgesProps) {
    const { t } = useTranslation();
    const analysis = session.status;
    const label = analysis ? analysisLabel(analysis, session.analysis_skipped_reason) : null;
    return (
        <div className="flex flex-wrap items-center gap-1.5 sm:justify-end">
            <SessionProgressBadge status={session.session_status} />
            {showAnalysis && label && (
                <span
                    className={`${PILL} ${label.style} inline-flex items-center gap-1`}
                    title={t("session.analysisStatus")}
                >
                    <Sparkles className="h-3 w-3" aria-hidden="true" />
                    <span className="sr-only">{t("session.analysisStatus")}: </span>
                    {t(label.key)}
                </span>
            )}
        </div>
    );
}
